/**
 * CollisionManager.js
 * Implements a simple deterministic collision manager for the specific need of this game
 */

import { Game } from "pixi_core";

import { MathLine } from "./MathLine";
import { Bubble } from "../Bubble";
import { SettingsManager } from "../SettingsManager";

import {
    GameEvents,
    WALL_TOP_Y,
    WALL_BOTTOM_Y,
    WALL_LEFT_X,
    WALL_RIGHT_X,
    BUBBLE_RADIUS,
    DEFAULT_BUBBLE_PHYSIC_RADIUS
} from "../../Constants";

export class CollisionManager {
    /**
     * @public
     */
    constructor() {
        /** @private @type {Bubble[]} */
        this._bubbles = [];

        /** @private @type {Number} */
        this._leftmostBubbleX = 0;

        /** @private @type {MathLine} */
        this._wallTop = null;
        /** @private @type {MathLine} */
        this._wallBottom = null;
        /** @private @type {MathLine} */
        this._wallRight = null;

        // Collision lines for the aim line, exclusing the bubble physics radius
        /** @private @type {MathLine} */
        this._wallTopAim = null;
        /** @private @type {MathLine} */
        this._wallBottomAim = null;
        /** @private @type {MathLine} */
        this._wallRightAim = null;

        /** @private @type {MathLine} - Re-usable trajectory line for the fired bubble and aim line */
        this._trajectory = new MathLine(0, 0, 0, 0);

        /** @private @type {Number} */
        this._collisionRadius = DEFAULT_BUBBLE_PHYSIC_RADIUS;

        Game.on(GameEvents.eSETTINGSMANAGER_READY, this._onSettingsManagerReady, this);
    }

    /**
     * @private
     * @param {SettingsManager} settings
     */
    _onSettingsManagerReady(settings) {
        this._collisionRadius = settings.collisionRadius;
        this._resetCollisionLines();
    }

    /**
     * @private
     */
    _resetCollisionLines() {
        /** @private @type {MathLine} */
        this._wallTop = new MathLine(
            WALL_LEFT_X,
            WALL_TOP_Y + this._collisionRadius,
            WALL_RIGHT_X - this._collisionRadius,
            WALL_TOP_Y + this._collisionRadius
        );

        /** @private @type {MathLine} */
        this._wallBottom = new MathLine(
            WALL_LEFT_X,
            WALL_BOTTOM_Y - this._collisionRadius,
            WALL_RIGHT_X - this._collisionRadius,
            WALL_BOTTOM_Y - this._collisionRadius
        );

        /** @private @type {MathLine} */
        this._wallRight = new MathLine(
            WALL_RIGHT_X - this._collisionRadius,
            WALL_TOP_Y + this._collisionRadius,
            WALL_RIGHT_X - this._collisionRadius,
            WALL_BOTTOM_Y - this._collisionRadius
        );

        // Collision lines for the aim line, exclusing the bubble physics radius
        /** @private @type {MathLine} */
        this._wallTopAim = new MathLine(WALL_LEFT_X, WALL_TOP_Y, WALL_RIGHT_X, WALL_TOP_Y);

        /** @private @type {MathLine} */
        this._wallBottomAim = new MathLine(WALL_LEFT_X, WALL_BOTTOM_Y, WALL_RIGHT_X, WALL_BOTTOM_Y);

        /** @private @type {MathLine} */
        this._wallRightAim = new MathLine(WALL_RIGHT_X, WALL_TOP_Y, WALL_RIGHT_X, WALL_BOTTOM_Y);
    }

    /**
     * Adds a bubble to collision detection
     * @public
     * @param {Bubble} bubble
     */
    addBubble(bubble) {
        this._bubbles.push(bubble);
        this._bubbles.sort(this._sortBubbles);
        this._leftmostBubbleX = this._bubbles[0].x;
    }

    /**
     * Adds an array of bubbles to collision detection
     * @public
     * @param {Bubble[]} bubbles
     */
    addBubbles(bubbles) {
        this._bubbles = this._bubbles.concat(bubbles);
        this._bubbles.sort(this._sortBubbles);
        this._leftmostBubbleX = this._bubbles[0].x;
    }

    /**
     * Removes a bubble from collision detection
     * @public
     * @param {Bubble} bubble
     */
    removeBubble(bubble) {
        const src = this._bubbles.indexOf(bubble);
        if (src !== -1) {
            this._bubbles.splice(src, 1);
            this._bubbles.sort(this._sortBubbles);
            this._leftmostBubbleX = this._bubbles.length > 0 ? this._bubbles[0].x : 0;
        }
    }

    /**
     * Removes an array of bubbles from collision detection
     * @public
     * @param {Bubble[]} bubbles
     */
    removeBubbles(bubbles) {
        for (const bubble of bubbles) {
            const src = this._bubbles.indexOf(bubble);
            if (src !== -1) {
                this._bubbles.splice(src, 1);
            }
        }

        this._bubbles.sort(this._sortBubbles);
        this._leftmostBubbleX = this._bubbles.length > 0 ? this._bubbles[0].x : 0;
    }

    /**
     * @private
     * @param {Bubble} bubbleA
     * @param {Bubble} bubbleB
     */
    _sortBubbles(bubbleA, bubbleB) {
        if (bubbleA.x < bubbleB.x) return -1;
        if (bubbleA.x > bubbleB.x) return 1;
        return 0;
    }

    /**
     * Performs a deterministic collision test on the fired bubble against grid bubbles and walls
     * Can be used for step-by-step collision detection or full trajectory detection
     * @public
     * @param {Number} currentX - Bubble current position
     * @param {Number} currentY
     * @param {Number} targetX  - Bubble target position
     * @param {Number} targetY
     * @param {Number} radius   - Bubble hit radius (may be smaller than the visual radius)
     * @param {import("../../Constants").CollisionData} result   - Result data structure
     */
    bubbleHitTest(currentX, currentY, targetX, targetY, radius, result) {
        // Reset result/collision data
        result.hit = false;
        result.hitBubble = false;
        result.hitWall = false;
        result.hitSideWall = false;
        result.hitBackWall = false;
        result.bubble = null;
        result.contactX = 0;
        result.contactY = 0;

        // Trajectory line
        this._trajectory.reset(currentX, currentY, targetX, targetY);

        // x-position of the first contact
        let firstContactX = 0;

        // Minimal distance alongside the trajectory where there is a contact
        let minContactDistance = Infinity;

        const distanceBetweenBubbles = BUBBLE_RADIUS + radius;

        // Bubbles
        if (targetX + radius >= this._leftmostBubbleX - BUBBLE_RADIUS) {
            for (const bubble of this._bubbles) {
                // No need to test the bubble further to the right if we already have a hit
                if (result.hit && bubble.x > firstContactX + BUBBLE_RADIUS * 2) {
                    break;
                }

                // Distance between the trajectory and the bubble center
                const distance = this._trajectory.distanceToPoint(bubble.x, bubble.y);
                if (distance <= BUBBLE_RADIUS * 2) {
                    if (!result.hit) {
                        firstContactX = bubble.x;
                    }

                    // Distance alongside the trajectory to reach the point perpendicular to the bubble center
                    const intersectionDistance = this._trajectory.intersectionDistance(bubble.x, bubble.y);

                    // Distance alongside the trajectory where the fired bubble enters in contact
                    const contactDistance =
                        intersectionDistance - Math.sqrt(distanceBetweenBubbles * distanceBetweenBubbles - distance * distance);

                    if (contactDistance < minContactDistance) {
                        minContactDistance = contactDistance;

                        result.hit = true;
                        result.hitBubble = true;
                        result.bubble = bubble;

                        result.contactX = currentX + (contactDistance * (targetX - currentX)) / this._trajectory.length;
                        result.contactY = currentY + (contactDistance * (targetY - currentY)) / this._trajectory.length;
                    }
                }
            }
        }

        // If there is a hit with a bubble and contact is within wall bounds, no need to test walls
        if (
            result.hit &&
            result.contactY < WALL_BOTTOM_Y - this._collisionRadius &&
            result.contactY > WALL_TOP_Y + this._collisionRadius &&
            result.contactX < WALL_RIGHT_X - this._collisionRadius
        ) {
            return;
        }

        // Walls
        const intersectionWall =
            targetY - currentY > 0 ? this._wallBottom.intersection(this._trajectory) : this._wallTop.intersection(this._trajectory);
        if (intersectionWall) {
            result.hit = true;
            result.hitBubble = false;
            result.hitWall = true;
            result.hitSideWall = true;
            result.hitBackWall = false;
            result.bubble = null;
            result.contactX = intersectionWall.x;
            result.contactY = intersectionWall.y;
        } else {
            const intersectionWallRight = this._wallRight.intersection(this._trajectory);
            if (intersectionWallRight) {
                result.hit = true;
                result.hitBubble = false;
                result.hitWall = true;
                result.hitSideWall = false;
                result.hitBackWall = true;
                result.bubble = null;
                result.contactX = intersectionWallRight.x;
                result.contactY = intersectionWallRight.y;
            }
        }
    }

    /**
     * Clear used references
     * @public
     */
    clear() {
        this._bubbles = [];
    }
}
